/**
 * Snapshot-based undo/redo stack for the spreadsheet and presentation
 * editors. Editor state is immutable (every update replaces the object), so
 * snapshots are plain references — no cloning cost.
 *
 * `record(prevState, group)` is called with the state as it was *before* a
 * mutation. Consecutive records sharing the same non-null `group` coalesce
 * into one undo step (per-keystroke text edits, drag resizes), keeping the
 * first "before" snapshot of the burst.
 */
export function createEditorHistory(limit = 80) {
  let undoStack = [];
  let redoStack = [];
  let lastGroup = null;

  return {
    record(prevState, group = null) {
      if (!(group && group === lastGroup && undoStack.length)) {
        undoStack.push(prevState);
        if (undoStack.length > limit) undoStack.shift();
      }
      lastGroup = group;
      redoStack = [];
    },
    undo(currentState) {
      if (!undoStack.length) return null;
      redoStack.push(currentState);
      lastGroup = null;
      return undoStack.pop();
    },
    redo(currentState) {
      if (!redoStack.length) return null;
      undoStack.push(currentState);
      lastGroup = null;
      return redoStack.pop();
    },
    breakGroup() {
      lastGroup = null;
    },
    clear() {
      undoStack = [];
      redoStack = [];
      lastGroup = null;
    },
    canUndo: () => undoStack.length > 0,
    canRedo: () => redoStack.length > 0,
  };
}
